import { Drawer } from "vaul";
import { X } from "lucide-react";
import { CommentsList } from "./CommentsList";
import { CommentInputBar } from "./CommentInputBar";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/components/AuthProvider";
import { useStableAvatar } from "@/hooks/use-stable-avatar";
import { useAuthModal } from "@/hooks/use-auth-modal";
import { useVisualViewport } from "@/hooks/use-visual-viewport";
import { supabase } from "@/integrations/supabase/client";
import { Review } from "@/types/review";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetPortal,
  SheetOverlay,
} from "@/components/ui/sheet";

interface CommentsBottomSheetProps {
  bookId: number;
  isOpen: boolean;
  onClose: () => void;
}

export function CommentsBottomSheet({ bookId, isOpen, onClose }: CommentsBottomSheetProps) {
  const [replyTo, setReplyTo] = useState<Review | null>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const drawerWrapperRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const { currentProfile } = useStableAvatar();
  const { openAuthModal } = useAuthModal();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  
  // Track keyboard by transforming the entire drawer wrapper (mobile only)
  useVisualViewport(drawerWrapperRef.current, { enabled: isOpen && isMobile });

  // Check if user has completed the book
  const { data: hasCompletedBook, isLoading: isCheckingCompletion } = useQuery({
    queryKey: ['bookCompletion', bookId, session?.user?.id, currentProfile?.id],
    queryFn: async () => {
      if (!session?.user?.id || !currentProfile?.id) return false;

      try {
        const { data: sessionsData, error } = await supabase
          .from('reading_sessions')
          .select('*')
          .eq('user_id', session.user.id)
          .eq('profile_id', currentProfile.id)
          .eq('book_id', parseInt(bookId.toString()))
          .order('end_time', { ascending: false })
          .limit(1);
          
        if (error) return false;
        if (sessionsData && sessionsData.length > 0) {
          return sessionsData[0].current_page > 0;
        }
        return false;
      } catch (error) {
        return false;
      }
    },
    enabled: !!bookId && !!session?.user?.id && !!currentProfile?.id,
  });

  // Admin override - always allow commenting for development
  const isAdmin = false; // Set to true in production with proper admin check

  // Clear cache when sheet closes
  useEffect(() => {
    if (!isOpen) {
      queryClient.removeQueries({ queryKey: ['reviews', bookId] });
      setReplyTo(null);
    }
  }, [isOpen, bookId, queryClient]);

  // Handle comment/reply submission
  const handleSubmit = async ({ content, isAnonymous, rating, replyToId }: { content: string; isAnonymous: boolean; rating: number; replyToId?: string }) => {
    console.log('🔵 handleSubmit called:', { content, isAnonymous, rating, replyToId });
    
    if (!session || !currentProfile) {
      openAuthModal('Sign in to comment');
      throw new Error('Not authenticated');
    }
    
    // Only check book completion for new comments, not replies
    if (!replyToId && !hasCompletedBook && !isAdmin) {
      toast({
        title: "Complete the book first",
        description: "Please complete the book to leave a review",
        variant: "destructive",
      });
      throw new Error('Book not completed');
    }

    // Generate anonymous display name if needed
    let anonymousDisplayName = null;
    if (isAnonymous) {
      const adjectives = ['Happy', 'Curious', 'Thoughtful', 'Avid', 'Passionate', 'Wise', 'Creative'];
      const nouns = ['Reader', 'Bookworm', 'Scholar', 'Explorer', 'Enthusiast', 'Learner', 'Storyteller'];
      const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
      const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
      const randomNumber = Math.floor(Math.random() * 999) + 1;
      anonymousDisplayName = `${randomAdjective} ${randomNoun} ${randomNumber}`;
    }

    try {
      if (replyToId) {
        console.log('🟢 Submitting reply to review:', replyToId);
        // Handle reply submission
        const { error } = await supabase.from('review_replies').insert({
          review_id: replyToId,
          user_id: session.user.id,
          content,
          is_generated: false,
        });

        if (error) {
          console.error('🔴 Error adding reply:', error);
          toast({
            title: "Failed to add reply",
            description: error.message,
            variant: "destructive",
          });
          throw error;
        }

        console.log('✅ Reply posted successfully');
        toast({
          title: "Reply posted",
          description: "Your reply has been added",
        });
      } else {
        console.log('🟢 Submitting new comment');
        // Handle comment submission with optimistic update
        const optimisticItem = {
          id: `temp-${Date.now()}`,
          content,
          user_display_name: isAnonymous ? anonymousDisplayName : (currentProfile.name || 'Reader'),
          user_avatar_url: isAnonymous ? null : currentProfile.avatar_url,
          created_at: new Date().toISOString(),
          likes_count: 0,
          has_user_liked: false,
          replies: [],
          user_id: session.user.id,
          profile_id: currentProfile.id,
          book_id: bookId,
          rating: rating,
          updated_at: new Date().toISOString(),
          deleted_at: null,
          is_generated: false,
          is_anonymous: isAnonymous,
          anonymous_display_name: anonymousDisplayName,
        };

        queryClient.setQueryData(['reviews', bookId], (old: any) => {
          if (!old?.pages?.[0]) return old;
          return {
            ...old,
            pages: [[optimisticItem, ...old.pages[0]], ...old.pages.slice(1)],
          };
        });

        const { error } = await supabase.from('book_reviews').insert({
          user_id: session.user.id,
          profile_id: currentProfile.id,
          book_id: bookId,
          content,
          rating: rating,
          is_anonymous: isAnonymous,
          anonymous_display_name: anonymousDisplayName,
          user_avatar_url: currentProfile.avatar_url,
        });

        if (error) {
          console.error('🔴 Error adding comment:', error);
          toast({
            title: "Failed to post comment",
            description: error.message,
            variant: "destructive",
          });
          throw error;
        }
        
        console.log('✅ Comment posted successfully');
      }
      
      // Clear reply state
      setReplyTo(null);
      
      // Invalidate to get real data
      queryClient.invalidateQueries({ queryKey: ['reviews', bookId] });
    } catch (error) {
      console.error('🔴 Submission error:', error);
    }
  };

  // Render desktop side sheet or mobile bottom drawer
  if (!isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetPortal>
          <SheetOverlay className="z-[13000]" />
          <SheetContent 
            side="right" 
            className="w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl bg-[#1a1a1a] border-white/10 p-0 flex flex-col z-[13001]"
          >
            {/* Header */}
            <SheetHeader className="flex-shrink-0 px-6 py-4 border-b border-white/10">
              <SheetTitle className="text-xl font-semibold text-white text-left">Comments</SheetTitle>
            </SheetHeader>

            {/* Comments List - Scrollable Area */}
            <div className="flex-1 overflow-auto">
              <CommentsList 
                bookId={bookId}
                onReplyClick={(comment) => setReplyTo(comment)}
              />
            </div>

            {/* Sticky Input at Bottom */}
            <div className="flex-shrink-0 border-t border-white/10 bg-[#1a1a1a]/95 backdrop-blur-md">
              <CommentInputBar
                bookId={bookId}
                replyTo={replyTo}
                session={session}
                hasCompletedBook={hasCompletedBook ?? false}
                isCheckingCompletion={isCheckingCompletion}
                onSubmit={handleSubmit}
                onCancelReply={() => setReplyTo(null)}
                onAuthRequired={() => openAuthModal('Sign in to comment')}
              />
            </div>
          </SheetContent>
        </SheetPortal>
      </Sheet>
    );
  }

  // Mobile bottom drawer
  return (
    <Drawer.Root open={isOpen} onOpenChange={(open) => !open && onClose()} dismissible={true} shouldScaleBackground={false}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[13000]" />
        <div
          ref={drawerWrapperRef}
          className="fixed left-0 right-0 z-[13001]"
          style={{
            bottom: 0,
            transform: 'translate3d(0,0,0)',
            willChange: 'transform'
          }}
        >
          <Drawer.Content 
            className="flex flex-col bg-[#1a1a1a] rounded-t-3xl shadow-2xl"
            style={{
              height: '90vh',
              maxHeight: '90vh',
            }}
          >
            {/* Drag Handle */}
            <div className="flex-shrink-0 w-full flex justify-center pt-3 pb-2">
              <div className="w-12 h-1.5 bg-white/20 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex-shrink-0 flex items-center justify-between px-6 py-3 border-b border-white/10">
              <Drawer.Title className="text-xl font-semibold text-white">Comments</Drawer.Title>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-white/70" />
              </button>
            </div>

            {/* Comments List - Scrollable Area */}
            <div className="flex-1 overflow-auto">
              <CommentsList 
                bookId={bookId}
                onReplyClick={(comment) => setReplyTo(comment)}
              />
            </div>

            {/* Sticky Input Inside Drawer */}
            <div
              ref={inputWrapperRef}
              className="sticky bottom-0 inset-x-0 z-[13002] border-t border-white/10 bg-[#1a1a1a]/95 backdrop-blur-md"
              style={{
                bottom: 'env(safe-area-inset-bottom, 0px)',
                pointerEvents: 'auto'
              }}
              data-vaul-no-drag
              data-vaul-ignore-outside-clicks
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
            >
              <CommentInputBar
                bookId={bookId}
                replyTo={replyTo}
                session={session}
                hasCompletedBook={hasCompletedBook ?? false}
                isCheckingCompletion={isCheckingCompletion}
                onSubmit={handleSubmit}
                onCancelReply={() => setReplyTo(null)}
                onAuthRequired={() => openAuthModal('Sign in to comment')}
              />
            </div>
          </Drawer.Content>
        </div>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
